import { HdBodygraphCenter } from './hd-bodygraph.models';
import { HdCycleKey } from './hd-cycle-chart.models';
import { HdPlanetKey } from './planet-grid/hd-planet-display-config';

/**
 * Structuur-metadata voor de Cycles cheat sheet — 1:1 naar het Figma-design
 * "Human Design - CYCLES" (file GmKWOc9FTtOKtq4IJ7AMUU, frame 2544:1182,
 * geëxtraheerd 2026-08-10). Alle vertaalbare tekst leeft in de Transloco-scope
 * `human-design/cycles` (`assets/i18n/human-design/cycles/{lang}.json`) en wordt
 * gelezen via `HdGateInfoService.cyclesCheatSheet*In(...)`; dit bestand bevat
 * enkel de taal-onafhankelijke structuur (kleuren, FA-iconen, planeet-glyphs,
 * kolomindelingen).
 *
 * De drie main keynotes (personal/educational/environmental) beschrijven de
 * relatie tussen een transit-activatie en de eigen chart; de kaart-keys van de
 * returns hergebruiken bewust het bestaande {@link HdCycleKey}-vocabulaire zodat
 * het geavanceerd-tabblad (Cycles-sectie) dezelfde content kan tonen voor een
 * specifieke cycle chart.
 */

/** Main keynote van een transit-activatie t.o.v. de eigen chart. */
export type HdCycleMainKeynoteKey = 'personal' | 'educational' | 'environmental';

export interface HdCycleMainKeynoteInfo {
  readonly key: HdCycleMainKeynoteKey;
  /** Header-/stripe-kleur van de kaart (Figma hex). */
  readonly color: string;
  /** Bare FA-icoonnaam (gerenderd via `fas fa-…`) in de kaart-header. */
  readonly faIcon: string;
}

/** Volgorde = kaartvolgorde op de cheat sheet (rij 1, links → rechts). */
export const HD_CYCLE_MAIN_KEYNOTES: readonly HdCycleMainKeynoteInfo[] = [
  { key: 'personal', color: '#FF5959', faIcon: 'user-vneck' },
  { key: 'educational', color: '#1983BF', faIcon: 'user-graduate' },
  { key: 'environmental', color: '#00C165', faIcon: 'image' },
];

/** Planeet-groep zoals de cheat sheet ze bundelt onder de main keynotes. */
export type HdCyclePlanetGroupKey = 'direction' | 'people' | 'development';

export interface HdCyclePlanetGroupInfo {
  readonly key: HdCyclePlanetGroupKey;
  /** Bare FA-icoonnaam in de band. */
  readonly faIcon: string;
  /** Planeten waarvan de Astronomicon-glyphs rechts in de band staan. */
  readonly planets: readonly HdPlanetKey[];
}

/** Volgorde = bandvolgorde op de cheat sheet (rij 2, links → rechts). */
export const HD_CYCLE_PLANET_GROUPS: readonly HdCyclePlanetGroupInfo[] = [
  { key: 'direction', faIcon: 'route', planets: ['northNode', 'southNode'] },
  { key: 'people', faIcon: 'users', planets: ['moon', 'mercury', 'venus', 'mars'] },
  {
    key: 'development',
    faIcon: 'compass-drafting',
    planets: ['jupiter', 'saturn', 'uranus', 'neptune', 'pluto'],
  },
];

/**
 * Key van een grote cheat-sheet-kaart. De vier returns delen hun key met
 * {@link HdCycleKey}; `general` en `program` zijn pure info-kaarten.
 */
export type HdCyclesCheatSheetCardKey =
  | 'solarReturn'
  | 'general'
  | 'saturnReturn'
  | 'uranusOpposition'
  | 'chironReturn'
  | 'program';

export interface HdCyclesCheatSheetCardInfo {
  readonly key: HdCyclesCheatSheetCardKey;
  /** Bijbehorende cycle-key, of `null` voor de losse info-kaarten. */
  readonly cycleKey: HdCycleKey | null;
  /** Planeet waarvan de Astronomicon-glyph in de header staat; `null` → FA-icoon. */
  readonly glyphPlanet: HdPlanetKey | null;
  /** Bare FA-icoonnaam wanneer er geen planeet-glyph is. */
  readonly faIcon: string | null;
  /** Dubbelbrede kaart (2 van de 3 grid-kolommen) met 2 bullet-kolommen. */
  readonly wide: boolean;
}

/** Volgorde = kaartvolgorde op de cheat sheet (rijen 4-6, links → rechts). */
export const HD_CYCLES_CHEAT_SHEET_CARDS: readonly HdCyclesCheatSheetCardInfo[] = [
  { key: 'solarReturn', cycleKey: 'solarReturn', glyphPlanet: 'sun', faIcon: null, wide: true },
  { key: 'general', cycleKey: null, glyphPlanet: null, faIcon: 'user-hat-tie-magnifying-glass', wide: false },
  { key: 'saturnReturn', cycleKey: 'saturnReturn', glyphPlanet: 'saturn', faIcon: null, wide: true },
  { key: 'uranusOpposition', cycleKey: 'uranusOpposition', glyphPlanet: 'uranus', faIcon: null, wide: false },
  { key: 'chironReturn', cycleKey: 'chironReturn', glyphPlanet: 'chiron', faIcon: null, wide: false },
  { key: 'program', cycleKey: null, glyphPlanet: null, faIcon: 'solar-system', wide: true },
];

/**
 * Kolomindeling van de "Niet zelf center thema's"-kaart (3 kolommen van 3
 * centers, volgorde exact zoals op de cheat sheet).
 */
export const HD_CYCLE_CENTER_CONDITIONING_COLUMNS: readonly (readonly HdBodygraphCenter[])[] = [
  ['hart', 'solarplexus', 'zelf'],
  ['milt', 'ajna', 'hoofd'],
  ['root', 'sacraal', 'keel'],
];

/** Stripe-kleur van de drie planeet-groep-banden. */
export const HD_CYCLE_PLANET_GROUP_STRIPE = '#FFB800';
/** Stripe-kleur van de "Tegenkoming -vs- Vernieuwing"-kaart. */
export const HD_CYCLE_ENCOUNTER_RENEWAL_STRIPE = '#7263FE';
/** Donkere header-/stripe-kleur van de return- en info-kaarten. */
export const HD_CYCLE_CARD_DARK = '#3D414E';
/** FA-icoon van de "Tegenkoming -vs- Vernieuwing"-kaart. */
export const HD_CYCLE_ENCOUNTER_RENEWAL_FA_ICON = 'map-location-dot';
/**
 * FA-iconen van de losse Tegenkoming/Vernieuwing-keynotes: encounter draagt het
 * kaart-icoon van de cheat sheet; renewal heeft daar (nog) geen icoon —
 * `arrows-rotate` gekozen als hernieuwings-beeld (2026-08-10, finetune volgt).
 */
export const HD_CYCLE_ENCOUNTER_KIND_FA_ICONS: Readonly<Record<'encounter' | 'renewal', string>> = {
  encounter: 'map-location-dot',
  renewal: 'arrows-rotate',
};
/** FA-icoon van de "Niet zelf center thema's"-kaart. */
export const HD_CYCLE_CENTER_CONDITIONING_FA_ICON = 'head-side-gear';
